import { useEffect, useState, useCallback } from "react";
import { fetchJobs, fetchTrucks, updateJobStatus, clearCancelledJobs, clearCompletedJobs, releaseBookedJob } from "../lib/api";
import { useAuth } from "../lib/auth";
import JobCard from "../components/JobCard";
import AssignDialog from "../components/AssignDialog";
import CancelOnRouteDialog from "../components/CancelOnRouteDialog";
import EditJobSheet from "../components/EditJobSheet";
import JobDetailSheet from "../components/JobDetailSheet";
import BookForLaterDialog from "../components/BookForLaterDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../components/EmptyState";
import { hasCap, can } from "../lib/roles";

export default function JobsPage() {
  const { user } = useAuth();
  const isAdmin = hasCap(user, "admin");
  // Jun 2026 — emptying a bin is a permanent delete, so it follows the
  // owner's "Delete jobs and empty bins" switch.
  const canClearBins = can(user, "delete_jobs");
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [corJob, setCorJob] = useState(null);
  const [bookForLaterJob, setBookForLaterJob] = useState(null);


  const refresh = useCallback(async () => {
    const [j, t] = await Promise.all([fetchJobs(), fetchTrucks()]);
    setJobs(j);
    setTrucks(t);
  }, []);
  useEffect(() => {
    refresh();
    const onEvt = () => refresh();
    window.addEventListener("jobs:refresh", onEvt);
    return () => window.removeEventListener("jobs:refresh", onEvt);
  }, [refresh]);

  // Auto-refresh every 60s while the tab is visible. Without this, a job
  // that completes via a partner poller (SN/AA close event) or by a driver
  // action lands directly in History but appears missing from the
  // Completed tab here — the user reads that as "job skipped the tab".
  // Pause when the tab is hidden so we're not hitting the server all day
  // for a dispatcher who's on another screen; snap back on focus.
  useEffect(() => {
    let cancelled = false;
    const tick = () => { if (!cancelled && document.visibilityState === "visible") refresh(); };
    const iv = setInterval(tick, 60000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);
  const advance = async (job, next) => {
    await updateJobStatus(job.id, next);
    toast.success(`Marked ${next.replace("_", " ")}`);
    refresh();
  };

  const cancel = async (job) => {
    await updateJobStatus(job.id, "cancelled");
    toast.success("Job cancelled");
    refresh();
  };

  const releaseBooked = async (job) => {
    try {
      await releaseBookedJob(job.id);
      toast.success("Released into the pending queue");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to release booked job");
    }
  };

  const clearCancelled = async () => {
    try {
      const res = await clearCancelledJobs();
      toast.success(`Cleared ${res.deleted} cancelled job${res.deleted === 1 ? "" : "s"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear cancelled jobs");
    }
  };

  const clearCompleted = async () => {
    try {
      const res = await clearCompletedJobs();
      toast.success(`Cleared ${res.deleted} completed job${res.deleted === 1 ? "" : "s"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear completed jobs");
    }
  };

  // Feb 2026: merge the intermediate driver-flow states into the visible
  // dispatcher tabs so jobs never "disappear" from Jobs Queue:
  //   - `accepted` (driver tapped Accept, not yet moving) → Assigned tab
  //   - `awaiting_partner_close` (driver tapped Close, waiting for AA/FR
  //     confirmation) → On Site tab
  const groups = {
    pending: jobs.filter((j) => j.status === "pending"),
    booked: jobs.filter((j) => j.status === "booked"),
    assigned: jobs.filter((j) => ["assigned", "accepted"].includes(j.status)),
    en_route: jobs.filter((j) => j.status === "en_route"),
    on_site: jobs.filter((j) => ["on_site", "awaiting_partner_close"].includes(j.status)),
    completed: jobs.filter((j) => j.status === "completed"),
    cancelled: jobs.filter((j) => j.status === "cancelled"),
  };

  return (
    <div className="p-6 space-y-6" data-testid="jobs-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Jobs Queue</h1>
        <p className="text-sm text-slate-500 mt-1">All active and historical roadside calls.</p>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700" data-testid="jobs-tabs">
          {Object.entries(groups).map(([k, list]) => (
            <TabsTrigger key={k} value={k} className="font-semibold capitalize" data-testid={`jobs-tab-${k}`}>
              {k === "booked" ? "Tow Booked" : k.replace("_", " ")} ({list.length})
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(groups).map(([k, list]) => (
          <TabsContent key={k} value={k} className="mt-4">
            {k === "cancelled" && list.length > 0 && canClearBins && (
              <div className="flex justify-end mb-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-800 dark:hover:text-rose-200"
                      data-testid="clear-cancelled-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear All Cancelled ({list.length})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="clear-cancelled-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-black tracking-tight">
                        Clear all cancelled jobs?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete <span className="font-semibold">{list.length}</span> cancelled job{list.length === 1 ? "" : "s"} from the database. They will also disappear from History. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="clear-cancelled-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={clearCancelled}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="clear-cancelled-confirm-action"
                      >
                        Delete {list.length} Job{list.length === 1 ? "" : "s"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
            {k === "completed" && list.length > 0 && isAdmin && (
              <div className="flex justify-end mb-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-800 dark:hover:text-rose-200"
                      data-testid="clear-completed-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear All Completed ({list.length})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="clear-completed-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-black tracking-tight">
                        Admin emergency cleanup — Clear all completed jobs?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Day-to-day, completed jobs should be <strong>archived</strong> by the billing user after they invoice (via History → Archive). This button is the admin-only escape hatch — it hard-deletes <span className="font-semibold">{list.length}</span> completed job{list.length === 1 ? "" : "s"} including photos and removes them from History. <span className="font-semibold text-rose-700">Cannot be undone.</span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="clear-completed-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={clearCompleted}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="clear-completed-confirm-action"
                      >
                        Delete {list.length} Job{list.length === 1 ? "" : "s"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.length === 0 && (
                <div className="col-span-full">
                  <EmptyState
                    icon={ClipboardList}
                    title="No jobs in this state"
                    hint="Jobs move through pending → assigned → en route → on site → completed as your fleet works through the day."
                    testId={`jobs-empty-${k}`}
                  />
                </div>
              )}
              {list.map((j) => (
                <div key={j.id} className="space-y-2">
                  <JobCard
                    job={j}
                    trucks={trucks}
                    dense
                    onAssign={(jj) => setAssignFor(jj)}
                    onAdvance={advance}
                    onEdit={(jj) => setEditJob(jj)}
                    onCancel={cancel}
                    onCancelOnRoute={(jj) => setCorJob(jj)}
                    onBookForLater={(jj) => setBookForLaterJob(jj)}
                    onCardClick={(jj) => setDetailJob(jj)}
                  />
                  {k === "booked" && (
                    <Button
                      size="sm"
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white font-semibold"
                      onClick={() => releaseBooked(j)}
                      data-testid={`release-booked-${j.id}`}
                    >
                      Release to queue now
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <AssignDialog
        job={assignFor}
        trucks={trucks}
        jobs={jobs}
        open={!!assignFor}
        onOpenChange={(o) => !o && setAssignFor(null)}
        onAssigned={refresh}
      />

      <EditJobSheet
        job={editJob}
        open={!!editJob}
        onOpenChange={(o) => !o && setEditJob(null)}
        onSaved={refresh}
      />

      <JobDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        truckById={Object.fromEntries(trucks.map((t) => [t.id, t]))}
      />

      <CancelOnRouteDialog
        open={!!corJob}
        job={corJob}
        onOpenChange={(o) => !o && setCorJob(null)}
        onDone={refresh}
      />

      <BookForLaterDialog
        open={!!bookForLaterJob}
        job={bookForLaterJob}
        onOpenChange={(o) => !o && setBookForLaterJob(null)}
        onBooked={refresh}
      />
    </div>
  );
}
